import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'
import { cn } from '../../lib/utils'

const resolveDocumentDir = (): 'ltr' | 'rtl' => {
  if (typeof document === 'undefined') return 'rtl'
  return document.documentElement.dir === 'ltr' ? 'ltr' : 'rtl'
}

const DropdownMenu = DropdownMenuPrimitive.Root
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
const DropdownMenuGroup = DropdownMenuPrimitive.Group
const DropdownMenuPortal = DropdownMenuPrimitive.Portal
const DropdownMenuSub = DropdownMenuPrimitive.Sub
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

/** Premium popover surface — hugs content, elevated, soft ring */
const menuSurfaceClass =
  'z-[250] w-max min-w-[13.5rem] max-w-[22rem] overflow-hidden rounded-2xl border border-border-subtle/80 bg-popover p-2 text-start text-popover-foreground shadow-modal ring-1 ring-black/5 dark:ring-white/10 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'

/** Icon-in-tile look for leading Lucide icons (direct SVG or inside asChild <a>) */
const leadingIconTileClass =
  '[&>svg:first-of-type]:pointer-events-none [&>svg:first-of-type]:box-content [&>svg:first-of-type]:size-4 [&>svg:first-of-type]:shrink-0 [&>svg:first-of-type]:rounded-lg [&>svg:first-of-type]:border [&>svg:first-of-type]:border-border-subtle/80 [&>svg:first-of-type]:bg-muted/70 [&>svg:first-of-type]:p-1.5 [&>svg:first-of-type]:text-foreground/80 [&>a>svg:first-of-type]:pointer-events-none [&>a>svg:first-of-type]:box-content [&>a>svg:first-of-type]:size-4 [&>a>svg:first-of-type]:shrink-0 [&>a>svg:first-of-type]:rounded-lg [&>a>svg:first-of-type]:border [&>a>svg:first-of-type]:border-border-subtle/80 [&>a>svg:first-of-type]:bg-muted/70 [&>a>svg:first-of-type]:p-1.5 [&>a>svg:first-of-type]:text-foreground/80'

const menuItemBaseClass = cn(
  'relative flex w-full cursor-default select-none items-center justify-start gap-3 rounded-xl px-2.5 py-2 text-sm font-medium outline-none transition-colors text-start',
  'focus:bg-muted/80 focus:text-foreground',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  'dark:focus:bg-[var(--primary-soft-bg)]',
  leadingIconTileClass,
  '[&_svg]:pointer-events-none',
)

const destructiveItemClass = cn(
  'text-destructive',
  'focus:bg-destructive/10 focus:text-destructive dark:focus:bg-destructive/15',
  '[&>svg:first-of-type]:border-destructive/20 [&>svg:first-of-type]:bg-destructive/10 [&>svg:first-of-type]:text-destructive',
  '[&.text-destructive]:text-destructive [&.text-destructive]:focus:bg-destructive/10 [&.text-destructive]:focus:text-destructive',
  '[&.text-destructive>svg:first-of-type]:border-destructive/20 [&.text-destructive>svg:first-of-type]:bg-destructive/10 [&.text-destructive>svg:first-of-type]:text-destructive',
)

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      menuItemBaseClass,
      'data-[state=open]:bg-muted/80',
      '[&>svg:last-of-type]:ms-auto [&>svg:last-of-type]:size-4 [&>svg:last-of-type]:border-0 [&>svg:last-of-type]:bg-transparent [&>svg:last-of-type]:p-0 [&>svg:last-of-type]:text-muted-foreground',
      inset && 'ps-8',
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRight className="rtl:rotate-180" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

type DropdownMenuSubContentProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubContent
> &
  Pick<React.HTMLAttributes<HTMLDivElement>, 'dir'>

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ className, collisionPadding = 16, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    collisionPadding={collisionPadding}
    className={cn(menuSurfaceClass, className)}
    {...{ dir: resolveDocumentDir(), ...props }}
  />
))
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

type DropdownMenuContentProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
> &
  Pick<React.HTMLAttributes<HTMLDivElement>, 'dir'>

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 6, collisionPadding = 16, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(menuSurfaceClass, className)}
      {...{ dir: resolveDocumentDir(), ...props }}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
    variant?: 'default' | 'destructive'
  }
>(({ className, inset, variant = 'default', ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    data-variant={variant}
    className={cn(
      menuItemBaseClass,
      variant === 'destructive' && destructiveItemClass,
      inset && 'ps-8',
      className,
    )}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-xl py-2.5 pe-3 ps-9 text-sm font-medium outline-none transition-colors text-start focus:bg-muted/80 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute start-3 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-xl py-2.5 pe-3 ps-9 text-sm font-medium outline-none transition-colors text-start focus:bg-muted/80 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute start-3 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'px-2.5 py-1.5 text-xs font-semibold tracking-wide text-muted-foreground text-start',
      inset && 'ps-8',
      className,
    )}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1.5 h-px bg-border-subtle/80', className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ms-auto text-xs font-normal tracking-widest text-muted-foreground', className)} {...props} />
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
